import { useEffect, useMemo, useRef, useState } from 'react';
import BottomSheet from '@rio-cloud/rio-uikit/BottomSheet';
import Button from '@rio-cloud/rio-uikit/Button';
import Checkbox from '@rio-cloud/rio-uikit/Checkbox';
import { CustomFormattedMessage } from '../../../i18n/i18n';
import { useDashboardFilteredTransactions } from '../../../state/transactions/transactionFilterSlice';
import { Transaction } from '../../../models';
import { Currency } from '../../../codegen/transactions';

const CURRENCY_EUR: Currency = 'EUR';
const LOCAL_STORAGE_CURRENCY_DISCLAIMER_DO_NOT_SHOW_AGAIN =
  'CURRENCY_DISCLAIMER_DO_NOT_SHOW_AGAIN';

const saveDoNotShowAgainState = (isDoNotShowAgain: boolean) => {
  localStorage.setItem(
    LOCAL_STORAGE_CURRENCY_DISCLAIMER_DO_NOT_SHOW_AGAIN,
    String(isDoNotShowAgain),
  );
};

const loadDoNotShowAgainState = (): boolean => {
  return (
    localStorage.getItem(
      LOCAL_STORAGE_CURRENCY_DISCLAIMER_DO_NOT_SHOW_AGAIN,
    ) === 'true'
  );
};

export const CurrencyDisclaimerPopup = () => {
  const {
    filteredTransactions: transactions,
    isLoading,
    error,
  } = useDashboardFilteredTransactions();

  const hasNonEuroTransaction = useMemo<boolean>(() => {
    if (isLoading || error) {
      return false;
    }
    const nonEuroTransactions: Transaction[] = transactions.filter(
      (transaction: Transaction) => {
        const dashboardAmountGross: number = Number(
          transaction.dashboardAmount?.amountGross,
        );
        const deliveryAmountCurrency: Currency | undefined =
          transaction.deliveryAmount?.currency;
        const completionAmountCurrency: Currency | undefined =
          transaction.completionAmount?.currency;
        return (
          dashboardAmountGross === 0 &&
          (deliveryAmountCurrency !== CURRENCY_EUR ||
            completionAmountCurrency !== CURRENCY_EUR)
        );
      },
    );
    return nonEuroTransactions.length !== 0;
  }, [transactions, isLoading, error]);

  const doNotShowAgainCheckboxRef = useRef<HTMLInputElement>(null);
  const [dismissed, setDismissed] = useState<boolean>(false);

  // Reset dismissed state in case of filter changes
  useEffect(() => {
    setDismissed(false);
  }, [transactions]);

  const doNotShowAgain: boolean = loadDoNotShowAgainState();

  const showPopup: boolean =
    hasNonEuroTransaction && !dismissed && !doNotShowAgain;

  const handleClosePopup = () => {
    setDismissed(true);
    saveDoNotShowAgainState(
      doNotShowAgainCheckboxRef.current?.checked ?? false,
    );
  };

  return (
    <BottomSheet
      show={showPopup}
      width={600}
      detach
      bodyClassName="padding-25 margin-right-25"
      onClose={handleClosePopup}
    >
      <div data-testid="currency-disclaimer-popup">
        <div>
          <CustomFormattedMessage
            id={'common.popup.currencyDisclaimer.description'}
          />
        </div>
        <div className="display-flex justify-content-between align-items-center margin-top-10">
          <Checkbox inputRef={doNotShowAgainCheckboxRef}>
            <CustomFormattedMessage
              id={'common.popup.currencyDisclaimer.doNotShowAgain'}
            />
          </Checkbox>
          <Button
            bsStyle={Button.PRIMARY}
            onClick={handleClosePopup}
          >
            <CustomFormattedMessage id={'common.ok'} />
          </Button>
        </div>
      </div>
    </BottomSheet>
  );
};
